import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { formatSearchString } from '@sorare/core/src/lib/string/formatSearchString';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionGradientWrapper } from 'components/collections/CollectionPreview/CollectionGradientWrapper';
import { CollectionPreviewHeader } from 'components/collections/CollectionPreview/CollectionPreviewHeader';
import { EmptyCollection } from 'components/collections/CollectionPreview/EmptyCollection';
import { Shimmer } from 'components/collections/CollectionPreview/Shimmer';
import { useCardCollectionData } from 'components/collections/CollectionPreview/useCardCollectionData';
import { CollectionSlot } from 'components/myCards/LeagueCollectionPreview/CollectionSlot';

import type { CommonCollectionPreview_cardCollection } from './__generated__/index.graphql';

export type { CommonCollectionPreview_cardCollection };

const Root = styled(Vertical)`
  border-radius: var(--double-unit);
  height: 100%;
  background: var(--c-black);
  overflow: hidden;
`;

const SlotsGrid = styled.div`
  display: grid;
  gap: var(--half-unit);
  padding: var(--double-unit);
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media ${laptopAndAbove} {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

type Props = {
  collection: CommonCollectionPreview_cardCollection;
  to?: string;
  playerNameFilter?: string;
};

export const CommonCollectionPreview = ({
  collection,
  to,
  playerNameFilter,
}: Props) => {
  const { userCardCollection, rarity, team } = collection;
  const { complete } = userCardCollection || {};
  const { hexColor } = useCardCollectionData(collection);

  if (!team) {
    return null;
  }

  const { fulfilledSlotsCount = 0 } = userCardCollection || {};
  if (!fulfilledSlotsCount) {
    return <EmptyCollection cardCollection={collection} to={to} />;
  }

  const normalizedFilter = formatSearchString(playerNameFilter);
  const slotsToDisplay =
    userCardCollection?.slots.filter(slot => {
      if (!normalizedFilter) {
        return true;
      }
      return formatSearchString(slot.slot?.anyPlayer?.displayName)?.includes(
        normalizedFilter
      );
    }) || [];

  return (
    <CollectionGradientWrapper
      className={classNames({ complete })}
      style={
        {
          '--completed-color': hexColor,
        } as CSSProperties
      }
    >
      <Root>
        <CollectionPreviewHeader collection={collection} to={to} />
        <SlotsGrid>
          {slotsToDisplay.map(slot => (
            <CollectionSlot
              key={slot.slug}
              userCardCollectionSlot={slot}
              rarity={rarity}
              small
            />
          ))}
        </SlotsGrid>
        {!!complete && <Shimmer rarity={rarity} />}
      </Root>
    </CollectionGradientWrapper>
  );
};

CommonCollectionPreview.fragments = {
  cardCollection: gql`
    fragment CommonCollectionPreview_cardCollection on CardCollection {
      slug
      rarity
      inSeason
      snapshotted
      team: anyTeam {
        slug
        name
        shortName
        pictureUrl
        styleGuideColor
      }
      userCardCollection(forUserSlug: $user) {
        id
        fulfilledSlotsCount
        complete
        slots {
          slug
          slot {
            id
            anyPlayer {
              slug
              displayName
            }
          }
          ...CollectionSlot_userCardCollectionSlot
        }
      }
      ...CollectionPreviewHeader_cardCollection
      ...useCardCollectionData_cardCollection
      ...EmptyCollection_cardCollection
    }
    ${CollectionPreviewHeader.fragments.cardCollection}
    ${CollectionSlot.fragments.userCardCollectionSlot}
    ${useCardCollectionData.fragments.cardCollection}
    ${EmptyCollection.fragments.cardCollection}
  ` as TypedDocumentNode<CommonCollectionPreview_cardCollection>,
};
